import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

export type LicensePrice = {
  id: string;
  tier: string;
  plan: string | null;
  effective_date: string;
  monthly_price: string;
  region: string;
  // Echoed from the region so a row renders without a lookup.
  currency: string;
  set_by: string;
  notes: string;
};

export type LicenseTier = {
  slug: string;
  name: string;
  blurb: string;
  organizations: number;
  current: Record<string, LicensePrice>;
  prices: LicensePrice[];
};

export type EnterprisePlanRow = {
  id: string;
  slug: string;
  name: string;
  description: string;
  features: string[];
  is_active: boolean;
  organizations: number;
  license_prices: LicensePrice[];
  region: string;
  region_slug: string;
  region_name: string;
  region_currency: string;
};

export type LicenseFeature = { slug: string; name: string; description: string };

export type LicenseState = {
  tiers: LicenseTier[];
  plans: EnterprisePlanRow[];
  features: LicenseFeature[];
};

export const QK_LICENSING = ['licensing'];

export const STANDARD_TIER = 'small_business';

export function licenseError(err: any) {
  toast.error(
    err.response?.data?.error || err.response?.data?.features?.[0]
    || err.response?.data?.slug?.[0] || err.response?.data?.tier?.[0] || err.message,
  );
}

export function useLicensing() {
  return useQuery({
    queryKey: QK_LICENSING,
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/licensing/')).data as LicenseState;
    },
  });
}

export type LicenceScope = { tier: string } | { plan: string };

export function useLicensePrices() {
  const queryClient = useQueryClient();
  const changed = () => queryClient.invalidateQueries({ queryKey: QK_LICENSING });

  const add = useMutation({
    mutationFn: async (
      body: LicenceScope & {
        monthly_price: string;
        region: string;
        effective_date: string;
        notes: string;
      },
    ) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post('/global-admin/server-license-prices/', body);
    },
    onSuccess: () => { changed(); toast.success('Licence fee set.'); },
    onError: licenseError,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.delete(`/global-admin/server-license-prices/${id}/`);
    },
    onSuccess: () => { changed(); toast.success('Removed.'); },
    onError: licenseError,
  });

  return { add, remove };
}
